import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { 
  Video, Phone, FileText, Globe, Languages, Euro, Search, 
  Calendar, Clock, Star, MapPin, CheckCircle, AlertCircle,
  Users, TrendingUp, CreditCard, Shield, Stethoscope, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Specialties with French names
const SPECIALTIES = [
  { code: "general", name: "Médecine générale" },
  { code: "dermatology", name: "Dermatologie" },
  { code: "cardiology", name: "Cardiologie" },
  { code: "gynecology", name: "Gynécologie" },
  { code: "pediatrics", name: "Pédiatrie" },
  { code: "psychiatry", name: "Psychiatrie" },
  { code: "ophthalmology", name: "Ophtalmologie" },
  { code: "otolaryngology", name: "ORL" },
  { code: "neurology", name: "Neurologie" },
  { code: "gastroenterology", name: "Gastro-entérologie" },
  { code: "rheumatology", name: "Rhumatologie" },
  { code: "endocrinology", name: "Endocrinologie" },
  { code: "urology", name: "Urologie" },
  { code: "pneumology", name: "Pneumologie" },
  { code: "allergology", name: "Allergologie" },
  { code: "dentist", name: "Dentiste" },
];

// ==================== CONSULTATION SEARCH PAGE ====================
export function ConsultationSearch() {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchParams, setSearchParams] = useState({
    specialty: '',
    patient_languages: ['fr'], // Patient can speak multiple languages
    consultation_type: 'online',
    max_price: ''
  });

  // Load consultation info
  useEffect(() => {
    fetch(`${API_URL}/api/consultations/info`)
      .then(res => res.json())
      .then(data => setInfo(data))
      .catch(err => console.error('Error loading info:', err));
  }, []);

  // Toggle language selection
  const toggleLanguage = (langCode) => {
    const current = searchParams.patient_languages;
    if (current.includes(langCode)) {
      // Remove if already selected (but keep at least one)
      if (current.length > 1) {
        setSearchParams({
          ...searchParams,
          patient_languages: current.filter(l => l !== langCode)
        });
      }
    } else {
      // Add language
      setSearchParams({
        ...searchParams,
        patient_languages: [...current, langCode]
      });
    }
  };

  const handleSearch = async () => {
    // Validate specialty is selected
    if (!searchParams.specialty) {
      toast({ 
        title: 'Spécialité requise', 
        description: 'Veuillez sélectionner le type de médecin dont vous avez besoin',
        variant: 'destructive' 
      });
      return;
    }

    // Validate at least one language
    if (searchParams.patient_languages.length === 0) {
      toast({ 
        title: 'Langue requise', 
        description: 'Sélectionnez au moins une langue que vous parlez',
        variant: 'destructive' 
      });
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/consultations/search`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          specialty: searchParams.specialty,
          language: searchParams.patient_languages[0], // Primary language
          patient_languages: searchParams.patient_languages, // ALL languages patient speaks
          consultation_type: searchParams.consultation_type,
          max_price: searchParams.max_price ? parseFloat(searchParams.max_price) : null
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        toast({ title: 'Erreur', description: data.detail || 'Erreur de recherche', variant: 'destructive' });
        setProfessionals([]);
        return;
      }
      
      setProfessionals(data.professionals || []);
      
      toast({
        title: `${data.results_count} médecin(s) trouvé(s)`,
        description: data.results_count === 0 
          ? 'Aucun médecin vérifié disponible. Essayez une autre langue ou spécialité.' 
          : data.info || ''
      });
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleBook = async (professional) => {
    if (!token) {
      toast({ title: 'Connexion requise', description: 'Veuillez vous connecter pour réserver', variant: 'destructive' });
      return;
    }

    // Get tomorrow's date
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];

    try {
      const res = await fetch(`${API_URL}/api/consultations/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          professional_id: professional.id,
          consultation_type: searchParams.consultation_type,
          preferred_date: dateStr,
          preferred_time: '10:00',
          language: searchParams.language,
          origin_url: window.location.origin
        })
      });

      const data = await res.json();
      
      if (data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        toast({ title: 'Erreur', description: data.detail || 'Erreur de réservation', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Stethoscope className="h-8 w-8 text-primary" />
            Consultations en Ligne
          </h1>
          <p className="text-muted-foreground mt-2">
            Trouvez un médecin vérifié disponible pour une téléconsultation
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/consultations/tutorial/patient'}
          className="gap-2"
        >
          <Video className="h-4 w-4" />
          Voir le tutoriel
        </Button>
      </div>

      {/* ADVANTAGES SECTION */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Shield className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-900 dark:text-green-100">Médecins Vérifiés</p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Tous nos médecins sont inscrits à l'Ordre et vérifiés automatiquement
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Clock className="h-6 w-6 text-blue-600" />
              <div>
                <p className="font-semibold text-blue-900 dark:text-blue-100">RDV Immédiat</p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Pas de délai d'attente. Trouvez un médecin disponible maintenant.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950 dark:to-violet-950 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Globe className="h-6 w-6 text-purple-600" />
              <div>
                <p className="font-semibold text-purple-900 dark:text-purple-100">31 Pays Européens</p>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  Consultez un médecin belge, suisse, allemand... depuis la France.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cross-border Info Banner */}
      {info && (
        <Card className="mb-6 border-l-4 border-l-primary bg-muted/30">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Heart className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">
                  Pourquoi utiliser drbooking ?
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  <li>✓ Vous n'avez pas trouvé de RDV disponible près de chez vous</li>
                  <li>✓ Vous avez besoin d'un avis médical rapide d'un spécialiste vérifié</li>
                  <li>✓ Vous voyagez en Europe et avez besoin d'une consultation</li>
                  <li>✓ Vous préférez consulter dans votre langue natale</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Rechercher un médecin
          </CardTitle>
          <CardDescription>
            Sélectionnez la spécialité correspondant à votre besoin médical
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Spécialité *</Label>
              <Select
                value={searchParams.specialty}
                onValueChange={(v) => setSearchParams({...searchParams, specialty: v})}
              >
                <SelectTrigger className={!searchParams.specialty ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Choisir..." />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALTIES.map(spec => (
                    <SelectItem key={spec.code} value={spec.code}>
                      {spec.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!searchParams.specialty && (
                <p className="text-xs text-red-500 mt-1">Requis - Un dermato ne consulte pas pour l'ORL</p>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>Je parle * (sélectionnez toutes vos langues)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  { code: 'fr', name: 'Français' },
                  { code: 'en', name: 'English' },
                  { code: 'de', name: 'Deutsch' },
                  { code: 'es', name: 'Español' },
                  { code: 'it', name: 'Italiano' },
                  { code: 'pt', name: 'Português' },
                  { code: 'nl', name: 'Nederlands' },
                  { code: 'ar', name: 'العربية' },
                ].map(lang => (
                  <Button
                    key={lang.code}
                    type="button"
                    variant={searchParams.patient_languages.includes(lang.code) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleLanguage(lang.code)}
                    className={searchParams.patient_languages.includes(lang.code) 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-primary/10'}
                  >
                    {lang.name}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {searchParams.patient_languages.length} langue(s) sélectionnée(s) — Médecins parlant l'une de ces langues
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <Label>Type de consultation</Label>
              <Select
                value={searchParams.consultation_type}
                onValueChange={(v) => setSearchParams({...searchParams, consultation_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">
                    <span className="flex items-center gap-2"><Video className="h-4 w-4" /> Téléconsultation vidéo</span>
                  </SelectItem>
                  <SelectItem value="phone">
                    <span className="flex items-center gap-2"><Phone className="h-4 w-4" /> Consultation téléphonique</span>
                  </SelectItem>
                  <SelectItem value="second_opinion">
                    <span className="flex items-center gap-2"><FileText className="h-4 w-4" /> Avis médical écrit</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Prix max (€)</Label>
              <Input
                type="number"
                placeholder="Ex: 80"
                value={searchParams.max_price}
                onChange={(e) => setSearchParams({...searchParams, max_price: e.target.value})}
              />
            </div>
            
            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full" disabled={loading || !searchParams.specialty}>
                <Search className="h-4 w-4 mr-2" />
                {loading ? 'Recherche...' : 'Trouver un médecin'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {professionals.length > 0 && (
        <div className="mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            {professionals.length} médecin(s) disponible(s)
          </h2>
          <p className="text-sm text-muted-foreground">
            Tous vérifiés et parlant {searchParams.patient_languages.map(l => info?.supported_languages?.[l] || l).join(' ou ')}
          </p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {professionals.map((pro) => (
          <Card key={pro.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">{pro.verified_name || `Dr ${pro.last_name}`}</CardTitle>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                      <Shield className="h-3 w-3 mr-1" /> Vérifié
                    </Badge>
                  </div>
                  <CardDescription>{pro.specialty_name || pro.specialty}</CardDescription>
                </div>
                {pro.average_rating && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    {pro.average_rating.toFixed(1)}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {pro.country_name} ({pro.country_code})
                </div>
                <div className="flex items-center gap-2">
                  <Languages className="h-4 w-4 text-blue-500" />
                  <span className="text-blue-700 font-medium">
                    Parle: {pro.common_languages?.length > 0 
                      ? pro.common_languages.map(l => info?.supported_languages?.[l] || l).join(', ')
                      : pro.languages_spoken?.map(l => info?.supported_languages?.[l] || l).join(', ')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-xs">{pro.cross_border_reason}</span>
                </div>
              </div>
              
              {/* Available slots preview */}
              {pro.next_available_slots && pro.next_available_slots.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Prochains créneaux :
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {pro.next_available_slots.slice(0, 3).map((slot, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {slot.date} {slot.time}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-primary">{pro.patient_pays}€</p>
                  <p className="text-xs text-muted-foreground">TTC (frais inclus)</p>
                </div>
                <Button onClick={() => handleBook(pro)} data-testid={`book-${pro.id}`}>
                  <Calendar className="h-4 w-4 mr-1" />
                  Réserver
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {professionals.length === 0 && hasSearched && !loading && (
        <Card className="text-center py-12 border-dashed">
          <CardContent>
            <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">Aucun médecin vérifié disponible</p>
            <p className="text-muted-foreground mb-4">
              Aucun {SPECIALTIES.find(s => s.code === searchParams.specialty)?.name || 'médecin'} parlant {searchParams.patient_languages.map(l => info?.supported_languages?.[l] || l).join(' ou ')} n'est disponible actuellement.
            </p>
            <p className="text-sm text-muted-foreground">
              Essayez d'ajouter d'autres langues ou revenez plus tard.
            </p>
          </CardContent>
        </Card>
      )}
      
      {!hasSearched && (
        <Card className="text-center py-12">
          <CardContent>
            <Stethoscope className="h-12 w-12 mx-auto text-primary/50 mb-4" />
            <p className="text-lg font-medium">Trouvez votre médecin</p>
            <p className="text-muted-foreground">
              Sélectionnez une spécialité et votre langue pour trouver des médecins vérifiés disponibles
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== PROFESSIONAL SETTINGS PAGE ====================
export function ProfessionalConsultationSettings() {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [medicalOrderId, setMedicalOrderId] = useState('');
  const [countryCode, setCountryCode] = useState('FR');
  const [formData, setFormData] = useState({
    price_online: 50,
    price_phone: 40,
    price_second_opinion: 30,
    languages_spoken: ['fr'],
    accepts_international_eu: true,
    max_daily_consultations: 10
  });

  useEffect(() => {
    if (token) {
      loadVerificationStatus();
      loadSettings();
    }
  }, [token]);

  const loadVerificationStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/consultations/professional/verification-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setVerificationStatus(data);
    } catch (error) {
      console.error('Error loading verification status:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/consultations/professional/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings(data);
      if (data.settings) {
        setFormData({
          price_online: data.settings.price_online || 50,
          price_phone: data.settings.price_phone || 40,
          price_second_opinion: data.settings.price_second_opinion || 30,
          languages_spoken: data.settings.languages_spoken || ['fr'],
          accepts_international_eu: data.settings.accepts_international_eu ?? true,
          max_daily_consultations: data.settings.max_daily_consultations || 10
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/consultations/professional/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          consultation_types: ['online', 'phone', 'second_opinion'],
          ...formData
        })
      });

      const data = await res.json();
      
      if (data.success) {
        toast({ title: 'Paramètres enregistrés', description: 'Vos tarifs de consultation sont mis à jour' });
        loadSettings();
      } else {
        // Handle verification required error
        if (res.status === 403 && data.detail?.requires_verification) {
          toast({ 
            title: 'Vérification requise', 
            description: data.detail.error || 'Vous devez d\'abord soumettre votre numéro d\'inscription à l\'Ordre des Médecins',
            variant: 'destructive' 
          });
        } else {
          toast({ title: 'Erreur', description: data.detail?.error || data.error || 'Erreur de mise à jour', variant: 'destructive' });
        }
      }
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVerification = async () => {
    if (!medicalOrderId.trim()) {
      toast({ title: 'Erreur', description: 'Veuillez entrer votre numéro d\'inscription', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/consultations/professional/verify-medical-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          medical_order_id: medicalOrderId,
          country_code: countryCode
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast({ title: 'Vérification soumise', description: data.message || 'Votre demande sera traitée sous 24-48h' });
        loadVerificationStatus();
      } else {
        toast({ title: 'Erreur', description: data.detail || data.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'professional') {
    return (
      <Card className="max-w-lg mx-auto mt-8">
        <CardContent className="pt-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
          <p>Cette page est réservée aux professionnels de santé</p>
        </CardContent>
      </Card>
    );
  }

  // Show verification requirement first
  const isVerified = verificationStatus?.is_verified === true;
  const verificationPending = verificationStatus?.verification_status === 'pending';
  const specialtyAllowed = verificationStatus?.specialty_allowed !== false;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Video className="h-6 w-6 text-primary" />
          Paramètres Consultations en Ligne
        </h1>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/consultations/tutorial/professional'}
          className="gap-2"
        >
          <Video className="h-4 w-4" />
          Voir le tutoriel
        </Button>
      </div>

      {/* VERIFICATION STATUS BANNER */}
      {!isVerified && (
        <Card className="mb-6 border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300">
              <AlertCircle className="h-5 w-5" />
              Vérification Obligatoire
            </CardTitle>
            <CardDescription className="text-yellow-600 dark:text-yellow-400">
              {!specialtyAllowed 
                ? "Votre spécialité n'est pas autorisée pour la téléconsultation. Seuls les médecins diplômés peuvent pratiquer."
                : verificationPending 
                  ? "Votre demande est en cours de vérification (24-48h)"
                  : "Vous devez soumettre votre numéro d'inscription à l'Ordre des Médecins pour activer la téléconsultation"
              }
            </CardDescription>
          </CardHeader>
          {specialtyAllowed && !verificationPending && (
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Pays d'inscription</Label>
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FR">France (RPPS)</SelectItem>
                      <SelectItem value="BE">Belgique (INAMI)</SelectItem>
                      <SelectItem value="LU">Luxembourg (Matricule)</SelectItem>
                      <SelectItem value="DE">Allemagne (Arztnummer)</SelectItem>
                      <SelectItem value="CH">Suisse (GLN)</SelectItem>
                      <SelectItem value="ES">Espagne (Colegiado)</SelectItem>
                      <SelectItem value="IT">Italie (CF Medico)</SelectItem>
                      <SelectItem value="PT">Portugal (Cédula)</SelectItem>
                      <SelectItem value="NL">Pays-Bas (BIG)</SelectItem>
                      <SelectItem value="AT">Autriche (ÖÄK)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Numéro d'inscription à l'Ordre</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder={countryCode === 'FR' ? 'Ex: 10123456789' : 'Votre numéro'}
                      value={medicalOrderId}
                      onChange={(e) => setMedicalOrderId(e.target.value)}
                    />
                    <Button onClick={handleSubmitVerification} disabled={loading}>
                      {loading ? 'Envoi...' : 'Soumettre'}
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Nous vérifierons votre numéro auprès de l'Ordre des Médecins de votre pays. Cette étape est obligatoire pour garantir la sécurité des patients.
              </p>
            </CardContent>
          )}
          {verificationPending && (
            <CardContent>
              <div className="flex items-center gap-2 text-yellow-600">
                <Clock className="h-5 w-5 animate-pulse" />
                <span>Vérification en cours... Un administrateur validera votre inscription sous 24-48h.</span>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* VERIFIED BADGE */}
      {isVerified && (
        <Card className="mb-6 border-green-500 bg-green-50 dark:bg-green-950">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Médecin vérifié - Téléconsultation autorisée</span>
              <Badge variant="outline" className="ml-auto bg-green-100">
                {verificationStatus?.medical_order?.id_type}: {verificationStatus?.medical_order?.id}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pricing Card */}
        <Card className={!isVerified ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5" />
              Vos Tarifs
            </CardTitle>
            <CardDescription>
              Définissez vos prix de consultation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Consultation vidéo (€)</Label>
              <Input
                type="number"
                value={formData.price_online}
                onChange={(e) => setFormData({...formData, price_online: parseFloat(e.target.value)})}
                min={10}
                max={500}
                disabled={!isVerified}
              />
            </div>
            <div>
              <Label>Consultation téléphone (€)</Label>
              <Input
                type="number"
                value={formData.price_phone}
                onChange={(e) => setFormData({...formData, price_phone: parseFloat(e.target.value)})}
                min={10}
                max={300}
              />
            </div>
            <div>
              <Label>Avis médical écrit (€)</Label>
              <Input
                type="number"
                value={formData.price_second_opinion}
                onChange={(e) => setFormData({...formData, price_second_opinion: parseFloat(e.target.value)})}
                min={20}
                max={200}
              />
            </div>
          </CardContent>
        </Card>

        {/* Fee Preview Card */}
        {settings?.fee_example && (
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Aperçu des Revenus
              </CardTitle>
              <CardDescription>
                Pour une consultation à {settings.fee_example.your_price}€
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Patient paie</span>
                  <span className="font-bold text-lg">{settings.fee_example.patient_pays}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vous recevez</span>
                  <span className="font-bold text-lg text-green-600">{settings.fee_example.you_receive}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Commission plateforme</span>
                  <span>{settings.fee_example.platform_fee}€</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Disponibilité
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Consultations max par jour</Label>
              <Input
                type="number"
                value={formData.max_daily_consultations}
                onChange={(e) => setFormData({...formData, max_daily_consultations: parseInt(e.target.value)})}
                min={1}
                max={50}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="eu-accept"
                checked={formData.accepts_international_eu}
                onChange={(e) => setFormData({...formData, accepts_international_eu: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="eu-accept" className="cursor-pointer">
                Accepter les patients de toute l'UE
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Languages Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              Langues Parlées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {['fr', 'en', 'de', 'es', 'it', 'pt', 'nl', 'ar'].map(lang => (
                <Button
                  key={lang}
                  variant={formData.languages_spoken.includes(lang) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    const newLangs = formData.languages_spoken.includes(lang)
                      ? formData.languages_spoken.filter(l => l !== lang)
                      : [...formData.languages_spoken, lang];
                    setFormData({...formData, languages_spoken: newLangs});
                  }}
                >
                  {lang.toUpperCase()}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Button onClick={handleSave} className="mt-6" disabled={loading || !isVerified}>
        {loading ? 'Enregistrement...' : isVerified ? 'Enregistrer les paramètres' : 'Vérification requise'}
      </Button>
    </div>
  );
}

// ==================== MY CONSULTATIONS PAGE ====================
export function MyConsultations() {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      loadConsultations();
    }
  }, [token]);

  const loadConsultations = async () => {
    try {
      const endpoint = user?.role === 'professional' 
        ? '/api/consultations/professional/list'
        : '/api/consultations/patient/list';

      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setConsultations(data.consultations || []);
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'pending_payment': { variant: 'outline', label: 'En attente de paiement' },
      'paid': { variant: 'default', label: 'Payé' },
      'scheduled': { variant: 'secondary', label: 'Planifié' },
      'in_progress': { variant: 'default', label: 'En cours' },
      'completed': { variant: 'success', label: 'Terminé' },
      'cancelled': { variant: 'destructive', label: 'Annulé' }
    };
    const { variant, label } = variants[status] || { variant: 'outline', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Calendar className="h-6 w-6 text-primary" />
        Mes Consultations en Ligne
      </h1>

      {consultations.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Video className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">Aucune consultation</p>
            <p className="text-muted-foreground mb-4">
              {user?.role === 'professional' 
                ? 'Vous n\'avez pas encore de consultations réservées'
                : 'Recherchez un médecin pour réserver une consultation'}
            </p>
            {user?.role !== 'professional' && (
              <Button onClick={() => window.location.href = '/consultations/search'}>
                Trouver un médecin
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {consultations.map((consultation) => (
            <Card key={consultation.id}>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(consultation.status)}
                      {consultation.cross_border && (
                        <Badge variant="outline" className="gap-1">
                          <Globe className="h-3 w-3" /> Transfrontalier
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold">
                      {user?.role === 'professional' 
                        ? consultation.patient_name 
                        : consultation.professional_name}
                    </h3>
                    <div className="text-sm text-muted-foreground flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {consultation.preferred_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {consultation.preferred_time}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-primary">
                      {user?.role === 'professional' 
                        ? consultation.professional_receives 
                        : consultation.patient_total}€
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user?.role === 'professional' ? 'Vous recevez' : 'Total payé'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== ADMIN STATS PAGE ====================
export function AdminConsultationStats() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      loadStats();
    }
  }, [token]);

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/consultations/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Chargement...</div>;
  if (!stats) return <div>Erreur de chargement</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Statistiques Consultations en Ligne</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats.consultations.total}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{stats.consultations.completed}</p>
                <p className="text-sm text-muted-foreground">Terminées</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Globe className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{stats.consultations.cross_border}</p>
                <p className="text-sm text-muted-foreground">Transfrontalières</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-8 w-8 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">{stats.revenue.platform_fees}€</p>
                <p className="text-sm text-muted-foreground">Revenus plateforme</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default { ConsultationSearch, ProfessionalConsultationSettings, MyConsultations, AdminConsultationStats };
